import { Component } from '@theme/component';

/**
 * Shows "Get it between X and Y" from business-day settings. Dates are computed in the
 * shop's time zone (`data-shop-offset`, e.g. "+0100") so every visitor sees the same
 * range. The merchant template replaces [start]/[desde] and [end]/[hasta] with dates,
 * inserted as text nodes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} text
 *
 * @extends {Component<Refs>}
 */
class DeliveryEstimate extends Component {
  requiredRefs = ['text'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  #render() {
    const minDays = Number(this.dataset.minDays) || 0;
    const maxDays = Math.max(Number(this.dataset.maxDays) || 0, minDays);
    const closedDates = new Set(
      (this.dataset.closedDates ?? '')
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean)
    );

    let start = shopToday(this.dataset.shopOffset ?? '+0000');
    const cutoffHour = Number(this.dataset.cutoffHour);
    if (Number.isInteger(cutoffHour) && cutoffHour >= 0 && start.hour >= cutoffHour) {
      start.date = addDays(start.date, 1);
    }

    const isShippingDay = (/** @type {Date} */ date) =>
      !(this.dataset.skipWeekends === 'true' && isWeekend(date)) && !closedDates.has(isoDate(date));

    const from = addShippingDays(start.date, minDays, isShippingDay);
    const to = addShippingDays(start.date, maxDays, isShippingDay);

    const locale = document.documentElement.lang || undefined;
    /** @type {Intl.DateTimeFormatOptions} */
    const options =
      this.dataset.format === 'short'
        ? { day: 'numeric', month: 'short', timeZone: 'UTC' }
        : { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' };
    const formatter = new Intl.DateTimeFormat(locale, options);

    this.#fill(this.dataset.template ?? '', {
      start: formatter.format(from),
      end: formatter.format(to),
    });
    this.hidden = false;
  }

  /**
   * Writes the template into the text ref, wrapping the dates in <strong>.
   * @param {string} template
   * @param {{ start: string, end: string }} dates
   */
  #fill(template, dates) {
    const { text } = this.refs;
    text.replaceChildren();

    for (const part of template.split(/(\[(?:start|desde|end|hasta)\])/)) {
      const key = { '[start]': 'start', '[desde]': 'start', '[end]': 'end', '[hasta]': 'end' }[part];
      if (key) {
        const strong = document.createElement('strong');
        strong.textContent = dates[/** @type {'start' | 'end'} */ (key)];
        text.append(strong);
      } else if (part) {
        text.append(document.createTextNode(part));
      }
    }
  }
}

/**
 * Current date and hour in the shop's time zone. Dates are UTC midnights so day math
 * is not affected by the visitor's own time zone.
 * @param {string} offset - Like "+0100" or "-0500".
 * @returns {{ date: Date, hour: number }}
 */
function shopToday(offset) {
  const match = /^([+-])(\d{2})(\d{2})$/.exec(offset);
  const sign = match?.[1] === '-' ? -1 : 1;
  const minutes = match ? sign * (Number(match[2]) * 60 + Number(match[3])) : 0;
  const shopNow = new Date(Date.now() + minutes * 60000);

  return {
    date: new Date(Date.UTC(shopNow.getUTCFullYear(), shopNow.getUTCMonth(), shopNow.getUTCDate())),
    hour: shopNow.getUTCHours(),
  };
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/**
 * Moves forward `days` shipping days; with 0 days, today if it ships, otherwise the next shipping day.
 * @param {Date} date
 * @param {number} days
 * @param {(date: Date) => boolean} isShippingDay
 */
function addShippingDays(date, days, isShippingDay) {
  let current = new Date(date);
  let remaining = days;

  while (!isShippingDay(current)) current = addDays(current, 1);

  while (remaining > 0) {
    current = addDays(current, 1);
    if (isShippingDay(current)) remaining -= 1;
  }

  return current;
}

/** @param {Date} date */
function isWeekend(date) {
  const day = date.getUTCDay();
  return day === 0 || day === 6;
}

/** @param {Date} date */
function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
